/**
 * Media Uploader Hooks Consolidation
 *
 * Consolidates all media upload hooks into a single file:
 * - useConfigParser - UploaderConfigLoader class and hook
 * - useUploaderState - uploaderReducer, initialUploaderState, and hook
 * - useUploadInitiate - hook with UploadInitiatePayload type
 * - useStorageUpload - uploadToStorage function
 * - useUploadQueue - hook with types
 * - useUploadComplete - hook with UploadCompletePayload type
 */

import { useRef, useCallback, useMemo, useReducer } from 'react'
// @ts-ignore - js-yaml has no types
import yaml from 'js-yaml'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import type {
  UploaderConfig,
  UploaderLabels,
  ResourceContext,
  FileItem,
  UploaderState,
  UploaderAction,
  UploadResult,
  OverallProgress,
  InitiateUploadResponse,
  CompleteUploadResponse,
  StorageProvider,
} from '@/lib/mediaUploadTypes'
import { UploadError } from '@/lib/mediaUploadTypes'
import { calculateFileChecksum } from '@/lib/mediaUploadUtils'
import { isRetryable, MAX_RETRIES, getErrorMessage } from '@/lib/mediaUploadUtils'


// ============================================================================
// Logger (lazy initialization to avoid import.meta issues in Jest)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('hooks/useMediaUpload')
  }
  return _log
}

// ============================================================================
// SECTION 1: useConfigParser - YAML Configuration Parsing
// ============================================================================

// Default Labels
const DEFAULT_LABELS: UploaderLabels = {
  dropzone: 'Drop files here or click to browse',
  button: 'Select Files',
  uploading: 'Uploading...',
  calculating: 'Calculating checksum...',
  success: 'Upload complete',
  remove: 'Remove',
  retry: 'Retry',
}

// Types for Raw YAML Structure
interface RawUploaderYaml {
  uploader?: {
    allowedTypes?: string[]
    maxFileSize?: number
    maxFiles?: number
    multiFile?: boolean
    dragAndDrop?: boolean
    autoUpload?: boolean
    showPreview?: boolean
    showFileSize?: boolean
    maxConcurrentUploads?: number
    calculateChecksum?: boolean
    resourceContext?: ResourceContext
    labels?: Partial<UploaderLabels>
  }
}

/**
 * Loader class for parsing and validating uploader YAML configuration.
 * Based on the FormConfigLoader pattern.
 */
export class UploaderConfigLoader {
  /**
   * Normalize and validate an UploaderConfig object, applying defaults.
   *
   * @param config - UploaderConfig object (potentially partial)
   * @returns Validated UploaderConfig with defaults applied
   * @throws Error if required fields are missing
   */
  static normalizeConfig(config: UploaderConfig): UploaderConfig {
    // Validate required fields
    if (!config.allowedTypes || !Array.isArray(config.allowedTypes)) {
      throw new Error(
        'Invalid uploader configuration: missing required field "allowedTypes" (must be an array of MIME types)'
      )
    }

    if (config.allowedTypes.length === 0) {
      throw new Error(
        'Invalid uploader configuration: "allowedTypes" cannot be empty'
      )
    }

    if (typeof config.maxFileSize !== 'number' || config.maxFileSize <= 0) {
      throw new Error(
        'Invalid uploader configuration: missing or invalid required field "maxFileSize" (must be a positive number)'
      )
    }

    if (!config.resourceContext) {
      throw new Error(
        'Invalid uploader configuration: missing required field "resourceContext"'
      )
    }

    if (!config.resourceContext.entityType) {
      throw new Error(
        'Invalid uploader configuration: "resourceContext.entityType" is required'
      )
    }

    if (!config.resourceContext.formResource) {
      throw new Error(
        'Invalid uploader configuration: "resourceContext.formResource" is required'
      )
    }

    if (!config.resourceContext.destination) {
      throw new Error(
        'Invalid uploader configuration: "resourceContext.destination" is required'
      )
    }

    // Build the config with defaults for optional fields
    return {
      allowedTypes: config.allowedTypes,
      maxFileSize: config.maxFileSize,
      maxFiles: config.maxFiles,
      multiFile: config.multiFile,
      dragAndDrop: config.dragAndDrop,
      autoUpload: config.autoUpload ?? false,
      showPreview: config.showPreview ?? true,
      showFileSize: config.showFileSize ?? true,
      maxConcurrentUploads: config.maxConcurrentUploads ?? 3,
      calculateChecksum: config.calculateChecksum ?? true,
      resourceContext: config.resourceContext,
      labels: {
        ...DEFAULT_LABELS,
        ...config.labels,
      },
    }
  }

  /**
   * Parse YAML string into UploaderConfig with defaults applied.
   *
   * @param yamlContent - YAML content string (imported with ?raw)
   * @returns Parsed and validated UploaderConfig
   * @throws Error if required fields are missing
   */
  static loadUploaderConfig(yamlContent: string): UploaderConfig {
    try {
      const raw = yaml.load(yamlContent) as RawUploaderYaml

      // Handle both wrapped (uploader:) and unwrapped YAML structures
      const config = raw?.uploader ?? (raw as unknown as RawUploaderYaml['uploader'])

      if (!config) {
        throw new Error('Invalid uploader configuration: YAML content is empty or malformed')
      }

      // Validate required fields
      if (!config.allowedTypes || !Array.isArray(config.allowedTypes)) {
        throw new Error(
          'Invalid uploader configuration: missing required field "allowedTypes" (must be an array of MIME types)'
        )
      }

      if (config.allowedTypes.length === 0) {
        throw new Error(
          'Invalid uploader configuration: "allowedTypes" cannot be empty'
        )
      }

      if (typeof config.maxFileSize !== 'number' || config.maxFileSize <= 0) {
        throw new Error(
          'Invalid uploader configuration: missing or invalid required field "maxFileSize" (must be a positive number)'
        )
      }

      if (!config.resourceContext) {
        throw new Error(
          'Invalid uploader configuration: missing required field "resourceContext"'
        )
      }

      if (!config.resourceContext.entityType) {
        throw new Error(
          'Invalid uploader configuration: "resourceContext.entityType" is required'
        )
      }

      if (!config.resourceContext.formResource) {
        throw new Error(
          'Invalid uploader configuration: "resourceContext.formResource" is required'
        )
      }

      if (!config.resourceContext.destination) {
        throw new Error(
          'Invalid uploader configuration: "resourceContext.destination" is required'
        )
      }

      // Build the config with defaults for optional fields
      const uploaderConfig: UploaderConfig = {
        allowedTypes: config.allowedTypes,
        maxFileSize: config.maxFileSize,
        maxFiles: config.maxFiles,
        multiFile: config.multiFile,
        dragAndDrop: config.dragAndDrop,
        autoUpload: config.autoUpload ?? false,
        showPreview: config.showPreview ?? true,
        showFileSize: config.showFileSize ?? true,
        maxConcurrentUploads: config.maxConcurrentUploads ?? 3,
        calculateChecksum: config.calculateChecksum ?? true,
        resourceContext: config.resourceContext,
        labels: {
          ...DEFAULT_LABELS,
          ...config.labels,
        },
      }

      return uploaderConfig
    } catch (error: unknown) {
      if (error instanceof Error) {
        // Re-throw our validation errors as-is
        if (error.message.startsWith('Invalid uploader configuration:')) {
          throw error
        }
        // Wrap YAML parsing errors
        throw new Error(`Failed to parse uploader YAML: ${error.message}`)
      }
      throw new Error('Failed to parse uploader YAML: unknown error')
    }
  }
}

/**
 * React hook for parsing uploader configuration.
 * Accepts either a YAML string or a pre-parsed UploaderConfig object.
 * Memoizes the result to avoid re-parsing on every render.
 *
 * @param configInput - Raw YAML string (imported with ?raw) or UploaderConfig object
 * @returns Parsed UploaderConfig
 * @throws Error if configuration is invalid
 */
export function useConfigParser(configInput: string | UploaderConfig): UploaderConfig {
  return useMemo(() => {
    // If it's already an UploaderConfig object, validate and return with defaults
    if (typeof configInput === 'object' && configInput !== null) {
      return UploaderConfigLoader.normalizeConfig(configInput)
    }
    // Otherwise parse as YAML string
    return UploaderConfigLoader.loadUploaderConfig(configInput)
  }, [configInput])
}

// ============================================================================
// SECTION 2: useUploaderState - State Management
// ============================================================================

/**
 * Generate a client-side UUID for file tracking.
 */
function generateFileId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Initial state for the uploader.
 */
export const initialUploaderState: UploaderState = {
  files: [],
  activeUploadsCount: 0,
  completedCount: 0,
  failedCount: 0,
}

/**
 * Reducer for managing uploader state.
 *
 * @param state - Current uploader state
 * @param action - Action to process
 * @returns New uploader state
 */
export function uploaderReducer(
  state: UploaderState,
  action: UploaderAction
): UploaderState {
  switch (action.type) {
    case 'ADD_FILES': {
      const newFiles: FileItem[] = action.files.map((file) => ({
        id: generateFileId(),
        file,
        status: 'pending' as const,
        progress: 0,
        retryCount: 0,
      }))
      return {
        ...state,
        files: [...state.files, ...newFiles],
      }
    }

    case 'REMOVE_FILE': {
      const fileToRemove = state.files.find((f) => f.id === action.id)
      if (!fileToRemove) return state

      // Adjust counts based on file status
      let { completedCount, failedCount } = state
      if (fileToRemove.status === 'success') {
        completedCount = Math.max(0, completedCount - 1)
      } else if (fileToRemove.status === 'error') {
        failedCount = Math.max(0, failedCount - 1)
      }

      return {
        ...state,
        files: state.files.filter((f) => f.id !== action.id),
        completedCount,
        failedCount,
      }
    }

    case 'UPDATE_FILE': {
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id ? { ...f, ...action.updates } : f
        ),
      }
    }

    case 'START_UPLOAD': {
      // Increment activeUploadsCount and update file status
      return {
        ...state,
        activeUploadsCount: state.activeUploadsCount + 1,
        files: state.files.map((f) =>
          f.id === action.id ? { ...f, status: 'hashing' as const } : f
        ),
      }
    }

    case 'FINISH_UPLOAD': {
      // Decrement activeUploadsCount, but never below 0
      return {
        ...state,
        activeUploadsCount: Math.max(0, state.activeUploadsCount - 1),
      }
    }

    case 'UPLOAD_SUCCESS': {
      return {
        ...state,
        completedCount: state.completedCount + 1,
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                status: 'success' as const,
                result: action.result,
                progress: 100,
              }
            : f
        ),
      }
    }

    case 'UPLOAD_ERROR': {
      return {
        ...state,
        failedCount: state.failedCount + 1,
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                status: 'error' as const,
                error: action.error,
                errorCode: action.errorCode,
              }
            : f
        ),
      }
    }

    case 'RETRY_FILE': {
      return {
        ...state,
        failedCount: Math.max(0, state.failedCount - 1),
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                status: 'pending' as const,
                error: undefined,
                errorCode: undefined,
                retryCount: f.retryCount + 1,
              }
            : f
        ),
      }
    }

    case 'CLEAR_COMPLETED': {
      const completedFiles = state.files.filter((f) => f.status === 'success')
      return {
        ...state,
        files: state.files.filter((f) => f.status !== 'success'),
        completedCount: state.completedCount - completedFiles.length,
      }
    }

    case 'RESET': {
      return initialUploaderState
    }

    default:
      return state
  }
}

/**
 * Calculate overall progress from file states.
 */
function calculateOverallProgress(
  files: FileItem[],
  activeUploadsCount: number,
  completedCount: number,
  failedCount: number
): OverallProgress {
  const totalFiles = files.length
  if (totalFiles === 0) {
    return {
      totalFiles: 0,
      completedFiles: 0,
      failedFiles: 0,
      activeUploads: 0,
      overallPercent: 0,
    }
  }

  // Calculate weighted average progress
  const totalProgress = files.reduce((sum, file) => {
    if (file.status === 'success') return sum + 100
    if (file.status === 'error') return sum + 0
    return sum + (file.progress || 0)
  }, 0)

  const overallPercent = Math.round(totalProgress / totalFiles)

  return {
    totalFiles,
    completedFiles: completedCount,
    failedFiles: failedCount,
    activeUploads: activeUploadsCount,
    overallPercent,
  }
}

/**
 * Hook for managing uploader state with derived values.
 *
 * @param maxConcurrentUploads - Maximum concurrent uploads allowed (default: 3)
 * @returns State, dispatch, and derived values
 */
export function useUploaderState(maxConcurrentUploads: number = 3) {
  const [state, dispatch] = useReducer(uploaderReducer, initialUploaderState)

  // Derived state values
  const canStartUpload = useMemo(
    () => state.activeUploadsCount < maxConcurrentUploads,
    [state.activeUploadsCount, maxConcurrentUploads]
  )

  const hasUploadsInProgress = useMemo(
    () => state.activeUploadsCount > 0,
    [state.activeUploadsCount]
  )

  const allComplete = useMemo(
    () =>
      state.files.length > 0 &&
      state.files.every((f) => f.status === 'success' || f.status === 'error'),
    [state.files]
  )

  const overallProgress = useMemo(
    () =>
      calculateOverallProgress(
        state.files,
        state.activeUploadsCount,
        state.completedCount,
        state.failedCount
      ),
    [state.files, state.activeUploadsCount, state.completedCount, state.failedCount]
  )

  // Action dispatchers
  const addFiles = useCallback((files: File[]) => {
    dispatch({ type: 'ADD_FILES', files })
  }, [])

  const removeFile = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_FILE', id })
  }, [])

  const updateFile = useCallback((id: string, updates: Partial<FileItem>) => {
    dispatch({ type: 'UPDATE_FILE', id, updates })
  }, [])

  const startUpload = useCallback((id: string) => {
    dispatch({ type: 'START_UPLOAD', id })
  }, [])

  const finishUpload = useCallback((id: string) => {
    dispatch({ type: 'FINISH_UPLOAD', id })
  }, [])

  const uploadSuccess = useCallback((id: string, result: UploadResult) => {
    dispatch({ type: 'UPLOAD_SUCCESS', id, result })
  }, [])

  const uploadError = useCallback((id: string, error: string, errorCode?: string) => {
    dispatch({ type: 'UPLOAD_ERROR', id, error, errorCode })
  }, [])

  const retryFile = useCallback((id: string) => {
    dispatch({ type: 'RETRY_FILE', id })
  }, [])

  const clearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return {
    // State
    state,
    dispatch,

    // Derived values
    canStartUpload,
    hasUploadsInProgress,
    allComplete,
    overallProgress,

    // Action dispatchers
    addFiles,
    removeFile,
    updateFile,
    startUpload,
    finishUpload,
    uploadSuccess,
    uploadError,
    retryFile,
    clearCompleted,
    reset,
  }
}

// ============================================================================
// SECTION 3: useUploadInitiate - Initiate Upload
// ============================================================================

/**
 * Payload for the upload initiate request.
 */
export interface UploadInitiatePayload {
  fileName: string
  contentType: string
  fileSize: number
  checksum?: string
  resourceContext: {
    entityId: string
    entityType: string
    formResource: string
    destination: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

/**
 * Hook for initiating an upload and getting the pre-signed URL.
 *
 * Uses the useApiMutation pattern from existing hooks.
 *
 * @returns Mutation result with mutate/mutateAsync functions
 */
export function useUploadInitiate() {
  const log = getLog()

  const mutation = useApiMutation<InitiateUploadResponse, UploadInitiatePayload>(
    'uploadInitiate',
    'POST',
    {
      onSuccess: (data) => {
        log.info('Upload initiated successfully', {
          uploadId: data.uploadId,
          storageProvider: data.storageProvider,
        })
      },
      onError: (error) => {
        const errorMessage = error instanceof Error
          ? (error as Error).message
          : String(error)
        log.error('Upload initiation failed', { error: errorMessage })
      },
    }
  )

  return mutation
}

/**
 * Type for the initiate upload mutation result.
 */
export type UseUploadInitiateResult = ReturnType<typeof useUploadInitiate>

// ============================================================================
// SECTION 4: useStorageUpload - Storage Upload Implementation
// ============================================================================

/** Milestone percentages for debug logging */
const PROGRESS_MILESTONES = [25, 50, 75, 100]

/**
 * Shared XHR PUT implementation used by all storage providers.
 *
 * @param file - The File object to upload
 * @param uploadUrl - Pre-signed URL (S3) or SAS token URL (Azure Blob)
 * @param requiredHeaders - Headers from initiate response - set ALL of them
 * @param onProgress - Progress callback (0-100)
 * @param abortSignal - Optional AbortSignal for cancellation
 * @param successStatus - Expected success HTTP status (200 for S3, 201 for Azure)
 */
function xhrPut(
  file: File,
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  onProgress: (percent: number) => void,
  abortSignal: AbortSignal | undefined,
  successStatus: number
): Promise<void> {
  const log = getLog()
  let lastMilestone = 0

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)

        // Log at milestones
        for (const milestone of PROGRESS_MILESTONES) {
          if (percent >= milestone && lastMilestone < milestone) {
            log.debug(`Upload progress: ${milestone}%`)
            lastMilestone = milestone
          }
        }
      }
    }

    // Handle successful completion
    xhr.onload = () => {
      if (xhr.status === successStatus) {
        resolve()
      } else {
        reject(
          new UploadError(
            'NETWORK_ERROR',
            `Storage upload failed with status ${xhr.status}: ${xhr.statusText}`
          )
        )
      }
    }

    // Handle network errors
    xhr.onerror = () => {
      reject(new UploadError('NETWORK_ERROR', 'Network error during upload'))
    }

    // Handle abort
    xhr.onabort = () => {
      reject(new UploadError('NETWORK_ERROR', 'Upload was aborted'))
    }

    // Wire up abort signal
    if (abortSignal) {
      if (abortSignal.aborted) {
        // Already aborted
        reject(new UploadError('NETWORK_ERROR', 'Upload was aborted'))
        return
      }
      abortSignal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    // Open connection as PUT
    xhr.open('PUT', uploadUrl)

    // CRITICAL: Set ALL headers from requiredHeaders
    // Never hardcode any provider-specific header names or values
    for (const [key, value] of Object.entries(requiredHeaders)) {
      xhr.setRequestHeader(key, value)
    }

    // Send the file
    xhr.send(file)
  })
}

/**
 * Upload a file to storage using the appropriate provider implementation.
 *
 * This is the single public entry point that dispatches to the correct
 * internal implementation based on the storageProvider value.
 *
 * @param file - The File object to upload
 * @param provider - Storage provider from initiate response ('aws_s3' or 'azure_blob')
 * @param uploadUrl - Pre-signed URL or SAS token URL
 * @param requiredHeaders - All headers that must be set on the PUT request
 * @param onProgress - Progress callback (0-100)
 * @param abortSignal - Optional AbortSignal for cancellation
 * @throws UploadError with code 'UNSUPPORTED_PROVIDER' for unknown providers
 * @throws UploadError with code 'NETWORK_ERROR' for upload failures
 */
export async function uploadToStorage(
  file: File,
  provider: StorageProvider,
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  onProgress: (percent: number) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  const log = getLog()

  log.debug('Starting storage upload', {
    provider,
    fileName: file.name,
    fileSize: file.size,
    headerCount: Object.keys(requiredHeaders).length,
  })

  switch (provider) {
    case 'aws_s3':
      // AWS S3 PUT succeeds on HTTP 200
      return xhrPut(file, uploadUrl, requiredHeaders, onProgress, abortSignal, 200)

    case 'azure_blob':
      // Azure Blob PUT succeeds on HTTP 201
      return xhrPut(file, uploadUrl, requiredHeaders, onProgress, abortSignal, 201)

    default:
      // Unknown provider - this is NOT retryable
      throw new UploadError(
        'UNSUPPORTED_PROVIDER',
        `Unknown storage provider: ${provider as string}`
      )
  }
}

// ============================================================================
// SECTION 5: useUploadComplete - Complete Upload
// ============================================================================

/**
 * Payload for the upload complete request.
 * Note: Only uploadId is sent - NO success flag.
 */
export interface UploadCompletePayload {
  uploadId: string
  [key: string]: unknown
}

/**
 * Hook for completing an upload and verifying server-side.
 *
 * The server will check that the file exists in storage and matches
 * the expected size/checksum before returning a success response.
 *
 * @returns Mutation result with mutate/mutateAsync functions
 */
export function useUploadComplete() {
  const log = getLog()

  const mutation = useApiMutation<CompleteUploadResponse, UploadCompletePayload>(
    'uploadComplete',
    'POST',
    {
      onSuccess: (data) => {
        if (data.status === 'completed') {
          log.info('Upload completed and verified', {
            fileRecordId: data.fileRecord?.fileRecordId,
            objectKey: data.fileRecord?.objectKey,
          })
        } else if (data.status === 'failed') {
          log.error('Upload verification failed', {
            errorCode: data.error?.code,
            errorMessage: data.error?.message,
          })
          // Throw UploadError to be caught by caller
          throw new UploadError(
            data.error?.code ?? 'UPLOAD_NOT_VERIFIED',
            data.error?.message ?? 'Upload verification failed'
          )
        }
      },
      onError: (error) => {
        const errorMessage = error instanceof Error
          ? (error as Error).message
          : String(error)
        log.error('Upload complete request failed', { error: errorMessage })
      },
    }
  )

  /**
   * Wrapper for mutateAsync that handles the failed status case.
   * Throws UploadError if the server returns status: 'failed'.
   */
  const completeUpload = async (payload: UploadCompletePayload): Promise<CompleteUploadResponse> => {
    const response = await mutation.mutateAsync(payload)

    // Check if server verification failed
    if (response.status === 'failed') {
      throw new UploadError(
        response.error?.code ?? 'UPLOAD_NOT_VERIFIED',
        response.error?.message ?? 'Upload verification failed'
      )
    }

    return response
  }

  return {
    ...mutation,
    completeUpload,
  }
}

/**
 * Type for the complete upload mutation result.
 */
export type UseUploadCompleteResult = ReturnType<typeof useUploadComplete>

// ============================================================================
// SECTION 6: useUploadQueue - Queue Management and Processing
// ============================================================================

export interface UseUploadQueueOptions {
  /** Uploader configuration */
  config: UploaderConfig
  /** Entity ID files attach to */
  entityId: string
  /** State management from useUploaderState */
  uploaderState: ReturnType<typeof useUploaderState>
  /** Enable mock mode for development/testing */
  mockMode?: boolean
  /** Delay in ms for mock uploads (default: 1500) */
  mockDelay?: number
}

/**
 * Read a File as a base64 data URL so it can be stored and rendered as an
 * image src without needing a real S3 endpoint (dev/mock mode only).
 */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * Generate a mock upload result for a file.
 */
function generateMockResult(file: File, entityId: string, config: UploaderConfig): UploadResult {
  const mockUploadId = `up_mock_${crypto.randomUUID().replace(/-/g, '').substring(0, 24)}`
  const mockFileRecordId = `fr_mock_${crypto.randomUUID().replace(/-/g, '').substring(0, 24)}`
  const objectKey = `${config.resourceContext.destination}/${entityId}/${file.name}`

  return {
    uploadId: mockUploadId,
    fileRecordId: mockFileRecordId,
    fileName: file.name,
    objectKey,
    contentType: file.type,
    fileSize: file.size,
    checksum: 'mock_checksum_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16),
  }
}

/**
 * Simulate progress updates over time.
 */
function simulateProgress(
  onProgress: (progress: number) => void,
  durationMs: number
): Promise<void> {
  return new Promise((resolve) => {
    const steps = 10
    const stepDuration = durationMs / steps
    let currentStep = 0

    const interval = setInterval(() => {
      currentStep++
      const progress = Math.min(100, Math.round((currentStep / steps) * 100))
      onProgress(progress)

      if (currentStep >= steps) {
        clearInterval(interval)
        resolve()
      }
    }, stepDuration)
  })
}

/**
 * Hook for managing upload queue with concurrency control.
 *
 * @param options - Configuration and state management options
 * @returns Queue management functions
 */
export function useUploadQueue(options: UseUploadQueueOptions) {
  const { config, entityId, uploaderState, mockMode = false, mockDelay = 1500 } = options
  const log = getLog()

  // API hooks (not used in mock mode, but hooks must be called unconditionally)
  const initiateUpload = useUploadInitiate()
  const completeUpload = useUploadComplete()

  // Track AbortControllers per file for cancellation
  const abortControllers = useRef<Map<string, AbortController>>(new Map())

  // Track object URLs for cleanup
  const objectUrls = useRef<Map<string, string>>(new Map())

  /**
   * Register an object URL for later cleanup.
   */
  const registerObjectUrl = useCallback((fileId: string, url: string) => {
    objectUrls.current.set(fileId, url)
  }, [])

  /**
   * Revoke and cleanup object URL for a file.
   */
  const revokeObjectUrl = useCallback((fileId: string) => {
    const url = objectUrls.current.get(fileId)
    if (url) {
      URL.revokeObjectURL(url)
      objectUrls.current.delete(fileId)
    }
  }, [])

  /**
   * Cancel an in-progress upload.
   */
  const cancelUpload = useCallback((fileId: string) => {
    const controller = abortControllers.current.get(fileId)
    if (controller) {
      controller.abort()
      abortControllers.current.delete(fileId)
    }
    // Clean up object URL
    revokeObjectUrl(fileId)
  }, [revokeObjectUrl])

  /**
   * Mock upload a single file (simulates the real flow without API calls).
   */
  const mockUploadFile = useCallback(
    async (file: FileItem): Promise<void> => {
      const log = getLog()
      const { updateFile, uploadSuccess } = uploaderState

      log.info(`[MOCK MODE] Starting mock upload for ${file.file.name}`)

      try {
        // Step 1: Simulate checksum calculation
        if (config.calculateChecksum !== false) {
          updateFile(file.id, { status: 'hashing' })
          await new Promise((resolve) => setTimeout(resolve, mockDelay / 4))
          updateFile(file.id, { checksum: 'mock_checksum_' + file.id.substring(0, 8) })
        }

        // Step 2: Simulate initiate
        updateFile(file.id, { status: 'initiating' })
        await new Promise((resolve) => setTimeout(resolve, mockDelay / 8))

        // Step 3: Simulate upload with progress
        updateFile(file.id, { status: 'uploading', progress: 0 })
        await simulateProgress(
          (progress) => updateFile(file.id, { progress }),
          mockDelay / 2
        )

        // Step 4: Simulate verification
        updateFile(file.id, { status: 'verifying' })
        await new Promise((resolve) => setTimeout(resolve, mockDelay / 8))

        // Success - generate mock result, using data URL as objectKey so images
        // are immediately renderable without an S3 endpoint in dev mode.
        const mockResult = generateMockResult(file.file, entityId, config)
        const dataUrl = await fileToDataUrl(file.file)
        uploadSuccess(file.id, { ...mockResult, objectKey: dataUrl })
        log.info(`[MOCK MODE] Upload complete: ${file.file.name}`, {
          fileRecordId: mockResult.fileRecordId,
          objectKey: mockResult.objectKey,
        })

        // Clean up object URL on success
        revokeObjectUrl(file.id)
      } catch (error) {
        log.error(`[MOCK MODE] Unexpected error for ${file.file.name}`, error)
        uploaderState.uploadError(file.id, 'Mock upload failed unexpectedly', 'MOCK_ERROR')
      }
    },
    [config, entityId, mockDelay, uploaderState, revokeObjectUrl]
  )

  /**
   * Upload a single file through the complete pipeline.
   */
  const uploadFile = useCallback(
    async (file: FileItem): Promise<void> => {
      // Use mock upload if mock mode is enabled
      if (mockMode) {
        return mockUploadFile(file)
      }

      const log = getLog()
      const { updateFile, uploadSuccess, uploadError } = uploaderState

      // Create AbortController for this file
      const controller = new AbortController()
      abortControllers.current.set(file.id, controller)

      try {
        let checksum: string | undefined

        // Step 1: Calculate checksum (if enabled)
        if (config.calculateChecksum !== false) {
          updateFile(file.id, { status: 'hashing' })
          log.debug(`Calculating checksum for ${file.file.name}`)

          checksum = await calculateFileChecksum(file.file, (percent) => {
            // Optional: Update progress during hashing
          })

          updateFile(file.id, { checksum })
          log.debug(`Checksum calculated: ${checksum.substring(0, 16)}...`)
        }

        // Step 2: Initiate upload
        updateFile(file.id, { status: 'initiating' })
        log.debug(`Initiating upload for ${file.file.name}`)

        const initResponse = await initiateUpload.mutateAsync({
          fileName: file.file.name,
          contentType: file.file.type,
          fileSize: file.file.size,
          checksum,
          resourceContext: {
            ...config.resourceContext,
            entityId,
          },
        })

        // Step 3: Upload to storage
        updateFile(file.id, { status: 'uploading', progress: 0 })
        log.debug(`Uploading ${file.file.name} to ${initResponse.storageProvider}`)

        await uploadToStorage(
          file.file,
          initResponse.storageProvider,
          initResponse.uploadUrl,
          initResponse.requiredHeaders,
          (progress) => {
            updateFile(file.id, { progress })
          },
          controller.signal
        )

        // Step 4: Complete upload (server verification)
        updateFile(file.id, { status: 'verifying' })
        log.debug(`Verifying upload for ${file.file.name}`)

        const completeResponse = await completeUpload.completeUpload({
          uploadId: initResponse.uploadId,
        })

        // Success
        if (completeResponse.fileRecord) {
          uploadSuccess(file.id, completeResponse.fileRecord)
          log.info(`Upload complete: ${file.file.name}`, {
            fileRecordId: completeResponse.fileRecord.fileRecordId,
          })
        }

        // Clean up object URL on success
        revokeObjectUrl(file.id)
      } catch (error) {
        // Handle errors
        const uploadErr =
          error instanceof UploadError
            ? error
            : new UploadError('NETWORK_ERROR', (error as Error).message)

        const errorCode = uploadErr.code
        const errorMessage = getErrorMessage(errorCode) || uploadErr.message

        // Check if should retry
        const currentFile = uploaderState.state.files.find((f) => f.id === file.id)
        const retryCount = currentFile?.retryCount ?? 0

        if (isRetryable(errorCode) && retryCount < MAX_RETRIES) {
          // Format retry message
          const retryMessage = `${errorMessage} - Retry ${retryCount + 1}/${MAX_RETRIES}`
          log.warn(`Retryable error for ${file.file.name}`, {
            errorCode,
            retryCount: retryCount + 1,
          })

          // Dispatch retry
          uploaderState.uploadError(file.id, retryMessage, errorCode)
          uploaderState.retryFile(file.id)
        } else {
          // Non-retryable or max retries exceeded
          log.error(`Upload failed for ${file.file.name}`, {
            errorCode,
            message: errorMessage,
            retryCount,
          })
          uploaderState.uploadError(file.id, errorMessage, errorCode)
        }
      } finally {
        // Clean up AbortController
        abortControllers.current.delete(file.id)
      }
    },
    [
      mockMode,
      mockUploadFile,
      config,
      entityId,
      initiateUpload,
      completeUpload,
      uploaderState,
      revokeObjectUrl,
    ]
  )

  /**
   * Process the upload queue with concurrency control.
   *
   * Uses Promise.all pattern for concurrent uploads without stale closure issues.
   */
  const processUploadQueue = useCallback(
    async (files: FileItem[]): Promise<void> => {
      const log = getLog()
      const maxConcurrent = config.maxConcurrentUploads ?? 3

      // Filter to only pending files
      const pendingFiles = files.filter((f) => f.status === 'pending')

      if (pendingFiles.length === 0) {
        log.debug('No pending files to upload')
        return
      }

      log.info(`Starting upload queue`, {
        totalFiles: pendingFiles.length,
        maxConcurrent,
      })

      // Process files in batches of maxConcurrent
      for (let i = 0; i < pendingFiles.length; i += maxConcurrent) {
        const batch = pendingFiles.slice(i, i + maxConcurrent)

        // Start all uploads in this batch
        const uploadPromises = batch.map(async (file) => {
          uploaderState.startUpload(file.id)
          try {
            await uploadFile(file)
          } finally {
            uploaderState.finishUpload(file.id)
          }
        })

        // Wait for this batch to complete before starting the next
        await Promise.all(uploadPromises)
      }

      log.info(`Upload queue processing complete`)
    },
    [config.maxConcurrentUploads, uploaderState, uploadFile]
  )

  /**
   * Clean up all resources (object URLs, abort controllers).
   */
  const cleanup = useCallback(() => {
    // Abort all in-progress uploads
    for (const controller of abortControllers.current.values()) {
      controller.abort()
    }
    abortControllers.current.clear()

    // Revoke all object URLs
    for (const url of objectUrls.current.values()) {
      URL.revokeObjectURL(url)
    }
    objectUrls.current.clear()
  }, [])

  return {
    processUploadQueue,
    cancelUpload,
    registerObjectUrl,
    revokeObjectUrl,
    cleanup,
  }
}

/**
 * Type for the upload queue hook result.
 */
export type UseUploadQueueResult = ReturnType<typeof useUploadQueue>
